import { useMemo } from 'react';
import { classnames } from '@/libs/utils';

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

// Column gap between options, in px. Must match the `gap-2` (0.5rem) grid gap below —
// the thumb width/offset math depends on it to stay aligned for any option count.

/**
 * Presentational segmented control with a sliding selection thumb.
 * Used for the new layout placeholder controls; holds no store state of its own.
 */
export default function LayoutSegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled = false
}: {
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  const selectedIndex = useMemo(() => {
    const index = options.findIndex(opt => opt.value === value);
    return index === -1 ? 0 : index;
  }, [options, value]);

  return (
    <div className="flex items-center justify-between border-t py-3">
      <div className="text-sm font-medium">{label}</div>
      <div
        className={classnames(
          'relative flex gap-2 rounded-lg border bg-white',
          disabled ? 'pointer-events-none opacity-50' : ''
        )}
        role="radiogroup"
        aria-label={label}
        aria-disabled={disabled}
      >
        {/* Sliding selection thumb */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 rounded-md bg-slate-900 shadow-sm transition-transform duration-200 ease-out"
          style={{
            width: `calc((100%) / ${options.length})`,
            transform: `translateX(calc(${selectedIndex} * 100% ))`
          }}
        />
        {options.map(option => {
          const isSelected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={classnames(
                'relative z-10 rounded-md px-2 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed',
                isSelected ? 'text-slate-100' : 'text-black hover:text-slate-700'
              )}
            >
              <div className="px-1">{option.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
